import React, { useEffect, useState, useMemo } from 'react';
import { StyleSheet, View, Text, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import reportService from '../services/reportService';
import ReportList from '../components/reports/ReportList';
import ReportFilters from '../components/reports/ReportFilters';

const ReportListScreen = () => {
  const navigation = useNavigation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  // Cargar reportes
  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await reportService.getEnterpriseReports();
      console.log('Reportes cargados:', data);
      setReports(data);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      setError('No se pudieron cargar los reportes. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Cargar reportes al montar el componente
  useEffect(() => {
    loadReports();
    
    // Recargar reportes cuando la pantalla obtiene el foco
    const unsubscribe = navigation.addListener('focus', () => {
      loadReports();
    });
    
    return unsubscribe;
  }, [navigation]);

  // Manejar el refresco manual
  const handleRefresh = () => {
    setRefreshing(true);
    loadReports();
  };

  // Filtrar reportes según los filtros activos
  const filteredReports = useMemo(() => {
    if (Object.keys(activeFilters).length === 0) {
      return reports;
    }

    return reports.filter(report => {
      if (!report.tags || report.tags.length === 0) {
        return false;
      }

      // Verificar si el reporte coincide con todos los filtros activos
      return Object.entries(activeFilters).every(([category, selectedValues]) => {
        return report.tags.some(tag => 
          tag.category === category && selectedValues.includes(tag.value)
        );
      });
    });
  }, [reports, activeFilters]);

  // Manejar cambios en los filtros
  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
  };

  // Manejar la selección de un reporte
  const handleReportPress = (report) => {
    navigation.navigate('ReportDetail', { reportId: report.id });
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#2c3e50" />
        <Text style={styles.loadingText}>Cargando reportes...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Reportes de Misiones</Text>
        <TouchableOpacity style={styles.refreshButton} onPress={handleRefresh}>
          <Ionicons name="refresh" size={24} color="#3498db" />
        </TouchableOpacity>
      </View>
      
      {error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadReports}>
            <Text style={styles.retryButtonText}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          <ReportFilters 
            reports={reports}
            onFiltersChange={handleFiltersChange}
          />
          
          <Text style={styles.subtitle}>
            {filteredReports.length} de {reports.length} {reports.length === 1 ? 'reporte' : 'reportes'}
            {Object.keys(activeFilters).length > 0 && ' (filtrados)'}
          </Text>
          
          {refreshing && (
            <View style={styles.refreshingContainer}>
              <ActivityIndicator size="small" color="#3498db" />
              <Text style={styles.refreshingText}>Actualizando...</Text>
            </View>
          )}
          
          <ReportList reports={filteredReports} onReportPress={handleReportPress} />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 15,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: '#7f8c8d',
    fontSize: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  subtitle: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 15,
  },
  refreshButton: {
    padding: 5,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#3498db',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  retryButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  refreshingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  refreshingText: {
    marginLeft: 10,
    color: '#3498db',
    fontSize: 14,
  },
});

export default ReportListScreen;